import React, { FC, PropsWithChildren, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import { SideNav, SidenavItem, SidenavItemSection } from '@innovationnorway/component-library';

import css from './Page.module.scss';

import { resources } from 'src/i18n';

export interface LayoutWithSideNavProps {
  title?: React.ReactNode;
  menu?: SidenavItemSection[];
  children: React.ReactNode;
  basis?: number;
  //Exapand as needed
  asElement?: 'main' | 'div';
}

type SidenavKeys = keyof (typeof resources)['nb']['sidenav'];
const sidenavKeys = Object.keys(resources['nb']['sidenav']);

export function isSidenavKey(value: React.ReactNode): value is SidenavKeys {
  if (typeof value === 'string') {
    return sidenavKeys.includes(value);
  }

  return false;
}

const PageWithSideNav: FC<LayoutWithSideNavProps> = ({
  title,
  menu,
  children,
  basis,
  asElement = 'main',
}: PropsWithChildren<LayoutWithSideNavProps>) => {
  const { t: tSidenav } = useTranslation('sidenav');

  const Container = useCallback(
    (props) => {
      return React.createElement(asElement, { className: 'full-width-layout with-sidebar' }, props.children);
    },
    [asElement],
  );

  return (
    <Container>
      <div className={`margin-right--6 flex-basis--${basis}`}>
        <SideNav
          sectionTitle={title}
          sections={menu}
          renderLink={(sidenavItem: SidenavItem) => {
            return (
              <NavLink to={sidenavItem.href || '#'} className={({ isActive }) => (isActive ? 'active' : '')}>
                <span>{sidenavItem?.listLevel == null && sidenavItem.icon}</span>
                {sidenavItem?.listLevel != null && sidenavItem.listLevel}{' '}
                {isSidenavKey(sidenavItem.text) ? tSidenav(sidenavItem.text) : `${sidenavItem.text}`}
              </NavLink>
            );
          }}
        />
      </div>

      <div className={cn(css['page--side-nav__content'])}>{children}</div>
    </Container>
  );
};

export default PageWithSideNav;
